import { PlayIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "V2.0/common/button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { showErrorToast } from "V2.0/utils";
import { message, Tooltip } from "antd";
import { useCallback, useState } from "react";

function ConfirmResumePausedCalls(props) {
  const { onQueuePausedCalls, fetchCurrentQueueStats, onCancel, pausedCalls } =
    props;

  const [loading, setLoading] = useState<boolean>(false);

  const handleConfirmResumePausedCalls = useCallback(
    function () {
      const lifecycleCbs = {
        init: () => {
          setLoading(true);
          message.loading(`Resuming ${pausedCalls} Calls`);
        },
        success: (data: { message: string; requeuedCount: number }) => {
          const successMessage =
            data?.message ?? `${pausedCalls} calls Resumed`;

          setLoading(false);
          message.destroy();
          message.success(successMessage);

          fetchCurrentQueueStats();
          onCancel();
        },
        error: (e) => {
          message.destroy();
          showErrorToast(e, "Failed to resume calls");

          setLoading(false);
        },
      };

      onQueuePausedCalls(lifecycleCbs);
    },
    [onQueuePausedCalls, fetchCurrentQueueStats]
  );

  return (
    <Dialog
      open={true}
      onClose={onCancel}
    >
      <DialogTitle>{`Resume ${pausedCalls} calls?`}</DialogTitle>
      <DialogBody></DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmResumePausedCalls}
          loading={loading}
          disabled={loading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmCancelPausedCalls(props) {
  const {
    onCancelPausedCalls,
    fetchCurrentQueueStats,
    onCancel,
    pausedCalls,
  } = props;

  const [loading, setLoading] = useState<boolean>(false);

  const handleConfirmCancelPausedCalls = useCallback(
    function () {
      const lifecycleCbs = {
        init: () => {
          setLoading(true);
          message.loading(`Cancelling ${pausedCalls} Calls`);
        },
        success: (data: { message: string; requeuedCount: number }) => {
          const successMessage =
            data?.message ?? `${pausedCalls} calls Cancelled`;

          setLoading(false);
          message.destroy();
          message.success(successMessage);

          fetchCurrentQueueStats();
          onCancel();
        },
        error: (e) => {
          message.destroy();
          showErrorToast(e, "Failed to Cancel calls");

          setLoading(false);
        },
      };

      onCancelPausedCalls(lifecycleCbs);
    },
    [onCancelPausedCalls, fetchCurrentQueueStats],
  );

  return (
    <Dialog
      open={true}
      onClose={onCancel}
    >
      <DialogTitle>{`Cancel ${pausedCalls} calls?`}</DialogTitle>
      <DialogBody></DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmCancelPausedCalls}
          loading={loading}
          disabled={loading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PausedCallsLoading(props) {
  return (
    <div className="border rounded-lg w-full">
      <div className="h-8 rounded-t-lg p-2">
        <div className="animate-pulse bg-gray-100 h-4 w-24 rounded-sm"></div>
      </div>
      <div>
        <div className="flex items-baseline gap-x-2 p-4">
          <div className="animate-pulse bg-gray-100 h-10 w-10 p-4 rounded-sm" />
        </div>
      </div>
    </div>
  );
}

export default function PausedCalls(props) {
  const {
    isLoading,
    pausedCalls,
    onQueuePausedCalls,
    onCancelPausedCalls,
    fetchCurrentQueueStats,
    locked,
  } = props;

  const [openConfirmReumeCalls, setOpenConfirmResumeCalls] =
    useState<boolean>(false);

    const [openConfirmCancelCalls, setOpenConfirmCancelCalls] =
    useState<boolean>(false);

  if (isLoading) {
    return <PausedCallsLoading />;
  }

  return (
    <>
      <div className="border rounded-lg w-full">
        <div className="flex justify-between items-center bg-gray-50 px-4 rounded-t-lg">
          <p className="text-sm font-semibold">Paused</p>
          <div>
            <Tooltip title="Resume Calls">
              <Button
                plain
                disabled={pausedCalls === 0 || locked}
                onClick={() => setOpenConfirmResumeCalls(true)}
              >
                <PlayIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Cancel Calls">
              <Button
                plain
                disabled={pausedCalls === 0 || locked}
                onClick={() => setOpenConfirmCancelCalls(true)}
              >
                <TrashIcon />
              </Button>
            </Tooltip>
          </div>
        </div>
        <div className=" p-4 flex items-baseline gap-x-2">
          <p className="text-xl font-semibold text-right">{pausedCalls}</p>
          <p className="text-sm font-light">Calls</p>
        </div>
      </div>
      {openConfirmReumeCalls && (
        <ConfirmResumePausedCalls
          pausedCalls={pausedCalls}
          onCancel={() => setOpenConfirmResumeCalls(false)}
          onQueuePausedCalls={onQueuePausedCalls}
          fetchCurrentQueueStats={fetchCurrentQueueStats}
        />
      )}
      {openConfirmCancelCalls && (
        <ConfirmCancelPausedCalls
          pausedCalls={pausedCalls}
          onCancel={() => setOpenConfirmCancelCalls(false)}
          onCancelPausedCalls={onCancelPausedCalls}
          fetchCurrentQueueStats={fetchCurrentQueueStats}
        />
      )}
    </>
  );
}
